import React from 'react';
import map from 'lodash/map';
import { tabIDs } from './constants';
import { getUTCDateString, formatStartDate, formatEndDate } from 'helpers/dateHelper';
import {
    dataSources,
    DAY_IN_MS,
    chartColors,
    apiCallStatus,
    NotificationTypeCode,
} from 'helpers/constants';
import { ErrorBoundary } from 'sharedComponents';
import { getAssetDataSourceType } from 'helpers/assetAndPowertrainFilterHelper';
import { assetCondition } from '../../PowertrainConditionMonitoring/AssetOverviewDeatilsV2/constants';

import { EventLog } from './EventLog';
import { AssetDetails } from './AssetDetails';
import { ConditionInsights } from './ConditionInsights';
import { OperationalParameters as OperationalParametersV2 } from './OperationalParametersV2';
import { SensorProperties } from './SensorProperties';
import { AdvancedAnalyticsV2Lazy } from './AdvancedAnalyticsV2';
import AssetSummaryComponent from '../../PowertrainConditionMonitoring/AssetOverviewDeatilsV2/AssetSummary';
import { navigateToRoute, notificationGroupCodes } from '../../PowertrainOverview/Header/NotificationDropdown/helpers';
import { routes } from 'routes';
import { convertToCustomLocaleDate, formatTime } from 'helpers/dateTimeFormatter';
import { IconMaintenancePriority } from 'svgIcons/MotionPortalIcons';
import { lubricationRecommendation } from './AdvancedAnalyticsV2/RegreasingAdvice/constants';

// NOTE: In TabsV2.js these flags are *inverted* (many are "...Disabled" = !capability()).
// assetCapabilities are built as:
// - isAdvancedAnalyticsDisabled: !hasCBMCapability()
// - hasAdvancedAnalyticsV2Capability: !hasAdvancedAnalyticsV2Capability()  (name is misleading; true => disabled)
// - isRegreasingAdviceDisabled: !hasRegreasingAdviceCapability()
// - isCBMFeatureEnabled: feature flag
// - isMVDrive / isDeionizerMaintenance: positive (true => capability exists)
const getIsAdvancedAnalyticsV2Hidden = (assetCapabilities) => {
    if (!assetCapabilities) {
        return true; // Hide until capabilities are loaded
    }

    const caps = assetCapabilities;

    // Always show for these asset types
    const hasRegreasingAdvice = !caps.isRegreasingAdviceDisabled;
    const hasACS6080ConditionBasedMaintenance = !caps.isACS6080ConditionBasedMaintenance; /* ACS6080 Condition Based Maintenance capability */
    const hasIotPanelDriveConditionBasedMaintenance = caps.iotPanelDriveConditionBasedMaintenance;
    if (caps.isMVDrive || caps.isDeionizerMaintenance || hasRegreasingAdvice || hasACS6080ConditionBasedMaintenance || hasIotPanelDriveConditionBasedMaintenance) {
        return false;
    }

    // All three conditions must be true to show the tab
    const hasCBM = !caps.isAdvancedAnalyticsDisabled;
    const hasAdvAnalyticsV2 = !caps.hasAdvancedAnalyticsV2Capability; // inverted upstream
    const isEligible = caps.isCBMFeatureEnabled && hasCBM && hasAdvAnalyticsV2;

    return !isEligible;
};

export const getTabOptions = (translate, isSSAsset, overallStatus, assetCapabilities, asset, getFilteredAsset, onReportIssue) => {
    const checkIsMonitored = isSSAsset ? !asset?.isMonitored : false;
    const hasCustomSignalProfile = asset?.hasCustomSignalProfile;
    const isUnmonitoredWithCustomProfile = checkIsMonitored && hasCustomSignalProfile;
    const isTrendAndThresholdUnsupported = getFilteredAsset?.trend?.condition === assetCondition.CONDITION_UNSUPPORTED && getFilteredAsset?.threshold?.condition === assetCondition.CONDITION_UNSUPPORTED;
    return {
        summary: {
            tabID: tabIDs.summary,
            title: translate('ABB.Powertrain.Frontend.summaryTabTitle'),
            hidden: false,
            component: <AssetSummaryComponent asset={getFilteredAsset} isUnmonitored={checkIsMonitored} />
        },
        conditionInsights: {
            tabID: tabIDs.conditionInsights,
            title: translate('ABB.Powertrain.Frontend.conditionInsightsTab'),
            // TODO: Command this title icon showing as per PBI 420374
            // titleIcon: overallStatus !== undefined ? <IconCondition condition={overallStatus} /> : null,
            hidden: assetCapabilities?.hasConditionIndexNewCapability || assetCapabilities?.hasConditionDetailsCapability || isTrendAndThresholdUnsupported || checkIsMonitored,
            component: <ErrorBoundary key='error-boundary-condition-insights' onReportIssue={onReportIssue}><ConditionInsights asset={asset} /></ErrorBoundary>
        },
        advancedAnalyticsV2: {
            tabID: tabIDs.advancedAnalyticsV2,
            title: translate('ABB.Powertrain.Frontend.ptMaintenanceInsightsTabV2'),
            hidden: getIsAdvancedAnalyticsV2Hidden(assetCapabilities) || checkIsMonitored,
            titleIcon: <IconMaintenancePriority
                condition={lubricationRecommendation[getFilteredAsset?.maintenanceAdvice]?.iconID}
                width='16'
                height='16'
            />,
            component: <ErrorBoundary key='error-boundary-advanced-analytics' onReportIssue={onReportIssue}><AdvancedAnalyticsV2Lazy asset={asset} assetCapabilities={assetCapabilities} /></ErrorBoundary>
        },
        operationalParameters: {
            tabID: tabIDs.operationalParameters,
            title: translate('ABB.Powertrain.Frontend.detailedInformationOperationalParametersTabV2'),
            hidden: assetCapabilities?.isOperationalParameterV2Hidden || checkIsMonitored && !hasCustomSignalProfile,
            component: <ErrorBoundary key='error-boundary-operational-parameters' onReportIssue={onReportIssue}><OperationalParametersV2 asset={asset} isUnmonitored={isUnmonitoredWithCustomProfile} /></ErrorBoundary>
        },
        eventLog: {
            tabID: tabIDs.eventLog,
            title: translate('ABB.Powertrain.Frontend.detailedInformationEventLogTab'),
            hidden: assetCapabilities?.isEventLogHidden || checkIsMonitored,
            component: <ErrorBoundary key='error-boundary-event-log' onReportIssue={onReportIssue}><EventLog asset={asset} /></ErrorBoundary>
        },
        assetDetails: {
            tabID: tabIDs.assetDetails,
            title: translate('ABB.Powertrain.Frontend.detailedInformationAssetDetailsTab'),
            hidden: false,
            component: <AssetDetails asset={asset} isUnmonitored={checkIsMonitored} />
        },
        // COMMENTED AS LIMIT CONFIGURATION MOVED TO OPV2
        // limitsConfiguration: {
        //     tabID: tabIDs.limitsConfiguration,
        //     title: translate('ABB.Powertrain.Frontend.detailedInformationLimitsConfigurationTab'),
        //     route: routes.LimitsConfiguration,
        //     hidden: assetCapabilities.isLimitConfigHidden
        // },
        sensorProperties: {
            tabID: tabIDs.sensorProperties,
            title: translate('ABB.Powertrain.Frontend.detailedInformationSensorPropertiesTab'),
            hidden: !isSSAsset || checkIsMonitored,
            component: <ErrorBoundary key='error-boundary-sensor-properties' onReportIssue={onReportIssue}><SensorProperties asset={asset} /></ErrorBoundary>
        },
    };
};
// eslint-disable-next-line import/no-unused-modules
export const isSmSeAsset = (asset) => {
    return asset && getAssetDataSourceType(asset) === dataSources.SMARTSENSOR;
};
// eslint-disable-next-line import/no-unused-modules
export const getDefaultDateSelection = () => {
    const fromDate = new Date();
    fromDate.setTime(fromDate.getTime() - 7 * DAY_IN_MS);
    const formatedFromDate = formatStartDate(fromDate);

    const toDate = formatEndDate(new Date());
    toDate.setTime(toDate.getTime());

    return {
        from: getUTCDateString(formatedFromDate),
        to: getUTCDateString(toDate)
    };
};

// eslint-disable-next-line import/no-unused-modules
export const getInitialMultiTrendCharts = (preselectedKPIs) => {
    return map(preselectedKPIs, (kpi) => {
        return {
            chartName: '',
            chartID: kpi,
            kpiList: [{
                color: chartColors[0],
                values: [],
                kpiIdentifier: kpi
            }],
            zoom: { isZoomed: false, from: null, to: null },
            loadingStatus: apiCallStatus.SUCCESS,
        };
    });
};

export const handleNavigationFromNotificationV2 = (item, history) => {
    if (!item) {
        return;
    }
    const { notificationGroupCode, notificationTypeCode } = item;

    switch (notificationGroupCode) {
        case notificationGroupCodes.Event:
            return tabIDs.eventLog;
        case notificationGroupCodes.Condition:
            return tabIDs.conditionInsights;
        case notificationGroupCodes.Configuration:
            switch (notificationTypeCode) {
                case NotificationTypeCode.AssetNameplatePropertyChanged:
                case NotificationTypeCode.ParameterChanged:
                    return tabIDs.assetDetails;
                default:
                    return tabIDs.operationalParameters;
            }
        case notificationGroupCodes.Limits:
            return tabIDs.operationalParameters;
        case notificationGroupCodes.Maintenance:
            navigateToRoute(history, routes.MaintenanceOverviewV2);
            break;
        case notificationGroupCodes.SensorVitality:
        case notificationGroupCodes.Commissioning:
            return tabIDs.sensorProperties;
        case notificationGroupCodes.Subscription:
            navigateToRoute(history, routes.ToolsSubscriptionStatus, {}, { notificationData: item });
            break;
        case notificationGroupCodes.UserAction:
            return tabIDs.operationalParameters;
        case notificationGroupCodes.Connectivity:
            return tabIDs.summary;
        default:
            return tabIDs.summary; // Default to summary tab if no specific navigation is defined
    }
};

export const getXAxisLabelDateFormat = (tickValue) => {
    const isDayBoundary = tickValue && new Date(tickValue).getHours() === 0 && new Date(tickValue).getMinutes() === 0;
    const options = { day: 'numeric', month: 'numeric' };
    return isDayBoundary ? convertToCustomLocaleDate(tickValue, options)
        : formatTime(tickValue, { timeStyle: 'short' });
};
